import '@polymer/paper-dialog/paper-dialog.js';
import '@polymer/paper-checkbox/paper-checkbox.js';
import '@polymer/paper-tooltip/paper-tooltip.js';
import '@polymer/paper-item/paper-item.js';
import '../katapult-elements/katapult-button.js';
import '../katapult-drop-down/katapult-drop-down.js';
import '../style-modules/paper-dialog-style.js';
import { SquashNulls } from '../../modules/SquashNulls.js';
import { PolymerElement, html } from '@polymer/polymer';
import { Path } from '../../modules/Path.js';
import { Throttle } from '../../modules/Throttle.js';
import { getJobPermissionsDatabaseURL } from '../../modules/JobPermissionsShard.js';
import { isJobLocked, showLockedJobDialog } from '../katapult-maps-desktop/locked-job-dialog.js';
import { logger } from '@sentry/browser';

/* global Polymer, firebase */
class JobChooser extends PolymerElement {
  static get template() {
    return html`
      <style include="paper-tooltip-style paper-dialog-style">
        :host {
          position: relative;
          background-color: white;
        }
        #content {
          display: flex;
          flex-direction: row;
          align-items: center;
          height: 100%;
          width: 100%;
          position: relative;
          @apply (--job-chooser-content);
        }
        #dropdown {
          flex-grow: 1;
          @apply (--katapult-drop-down);
          --paper-input-container-underline: {
            display: var(--paper-input-container-underline-display, none);
          };
          --paper-input-container-disabled: {
            opacity: 1;
          };
        }
        #dropdown.underline {
          --paper-input-container-underline: {
            display: block;
          };
        }
        paper-tooltip {
          white-space: nowrap;
        }
        .headerItem {
          pointer-events: none;
          background-color: var(--paper-grey-200);
          color: var(--primary-text-color-faded);
          display: flex;
          justify-content: center;
        }
        .headerItem > iron-icon {
          color: var(--primary-text-color-faded);
          margin-right: 16px;
        }
        .headerItem > paper-spinner-lite {
          --paper-spinner-color: var(--secondary-color);
          height: 20px;
          width: 20px;
          margin-right: 16px;
          margin-left: -36px;
        }
        connection-state-icon {
          --connection-state-icon-color: var(--primary-text-color-faded);
        }
        #newJobDialog {
          min-width: 400px;
        }
      </style>

      <katapult-firebase-worker
        id="jobName"
        path="photoheight/jobs/[[selectedForListeners]]/name"
        data="{{jobName}}"
        disabled="[[firebaseDisabled]]"
      ></katapult-firebase-worker>
      <katapult-firebase-worker
        id="jobRecord"
        path="photoheight/job_permissions/[[userGroup]]/list/[[selectedForListeners]]"
        database-url="[[jobPermissionsDatabaseUrl]]"
        data="{{selectedJobRecord}}"
        disabled="[[firebaseDisabled]]"
      ></katapult-firebase-worker>
      <katapult-firebase-worker
        id="jobs"
        path="photoheight/job_permissions/[[userGroup]]/list"
        database-url="[[jobPermissionsDatabaseUrl]]"
        data="{{jobs}}"
        order-by-child="status"
        equal-to="active"
        disabled="[[!loadJobData]]"
        loading="{{loadingJobs}}"
        child-events
      ></katapult-firebase-worker>
      <katapult-firebase-worker
        id="jobChooserOptions"
        path="photoheight/company_space/[[userGroup]]/options/job_chooser"
        data="{{jobChooserOptions}}"
        disabled="[[firebaseDisabled]]"
      ></katapult-firebase-worker>
      <katapult-firebase-worker
        id="limitJobCreationToServerOwner"
        path="photoheight/server_options/list/limit_job_creation_to_server_owner"
        data="{{limitJobCreationToServerOwner}}"
        disabled="[[firebaseDisabled]]"
      ></katapult-firebase-worker>
      <project-folder-chooser
        id="projectFolderChooser"
        user-group="{{userGroup}}"
        job-id="[[selectedForListeners]]"
        company-names="{{companyNames}}"
        disabled="[[firebaseDisabled]]"
        on-toast="displayToastMessage"
        admin-reporting="{{adminReporting}}"
        job-chooser-options="{{jobChooserOptions}}"
        job-chooser-jobs="{{jobs}}"
      >
        <slot name="project-folder-chooser-folder-context-menu" slot="project-folder-chooser-folder-context-menu"></slot>
        <slot name="project-folder-chooser-job-context-menu" slot="project-folder-chooser-job-context-menu"></slot>
      </project-folder-chooser>
      <div id="content">
        <katapult-drop-down
          id="dropdown"
          class$="{{isUnderlined(underline)}}"
          items="[[items]]"
          value="{{selected}}"
          mobile="[[mobile]]"
          label-path="name"
          value-path="$key"
          null-value=""
          label="[[defaultText]]"
          item-icons="[[itemIcons]]"
          disabled="[[disabled]]"
          no-label-float="[[!labelFloat]]"
          only-open-down="[[onlyOpenDown]]"
          opened="{{opened}}"
          on-list-changed="dropdownListChanged"
          on-value-changed="trackSelectingJobs"
        >
          <template is="dom-if" if="[[loading]]">
            <paper-item class="headerItem" slot="aboveList">
              <paper-spinner-lite active=""></paper-spinner-lite>
              <span>LOADING JOBS</span>
            </paper-item>
          </template>
          <template is="dom-if" if="[[!loading]]">
            <template is="dom-if" if="[[offline]]">
              <paper-item class="headerItem" slot="aboveList">
                <connection-state-icon uploading="[[syncing]]"></connection-state-icon>
                <span hidden$="[[!syncing]]">SYNCING&nbsp;</span>
                <span>OFFLINE JOBS</span>
              </paper-item>
            </template>
            <template is="dom-if" if="[[!offline]]">
              <template is="dom-if" if="[[!hideArchivedButton]]">
                <paper-item on-click="toggleArchived">
                  <span hidden$="[[showArchived]]">SHOW ARCHIVED</span>
                  <span hidden$="[[!showArchived]]">HIDE ARCHIVED</span>
                </paper-item>
              </template>
              <template is="dom-if" if="[[userMayCreateJobs(noCreateNewJob, limitJobCreationToServerOwner, userGroup)]]">
                <paper-item on-tap="createNewJob">Create New Job</paper-item>
              </template>
            </template>
          </template>
        </katapult-drop-down>
        <template is="dom-if" if="{{calcShowEditButton(jobEditButton, selected)}}">
          <katapult-button
            id="openEditorButton"
            icon="settings"
            iconOnly
            noBorder
            job-id="{{selected}}"
            on-click="openJobEditor"
          ></katapult-button>
          <paper-tooltip for="openEditorButton">Job Settings</paper-tooltip>
        </template>
        <template is="dom-if" if="[[!hideFolderChooser]]">
          <katapult-button
            id="openFolderChooserButton"
            icon="folder"
            iconOnly
            noBorder
            loading="{{openingFolderChooser}}"
            on-click="openFolderChooser"
            noSuccess
            noError
          ></katapult-button>
          <paper-tooltip for="openFolderChooserButton" position="{{tooltipPosition}}">Open Job Chooser</paper-tooltip>
        </template>
      </div>
      <create-job-form
        id="createJobForm"
        user-group="[[userGroup]]"
        model-options="[[modelOptions]]"
        company-names="[[companyNames]]"
        on-choose-new-job-location="chooseNewJobLocation"
        is-review-contractor="[[isReviewContractor]]"
        other-attributes="[[otherAttributes]]"
        default-name="[[filterByName]]"
      >
      </create-job-form>
    `;
  }

  static get properties() {
    return {
      alphabetical: {
        type: Boolean,
        value: false,
        observer: 'updateSort'
      },
      // Controls whether the firebase data loads
      firebaseDisabled: {
        type: Boolean,
        value: false
      },
      loading: {
        type: Boolean,
        value: true,
        notify: true
      },
      opened: {
        type: Boolean,
        value: false,
        observer: 'openedChanged'
      },
      // Controls whether it is editable
      disabled: {
        type: Boolean,
        value: false
      },
      underline: {
        type: Boolean,
        value: false
      },
      hideFolderChooser: {
        type: Boolean,
        value: false
      },
      jobs: {
        type: Object,
        value: {},
        notify: true
      },
      labelFloat: {
        type: Boolean,
        value: false
      },
      /*can be used to force mobile dropdown*/
      mobile: {
        type: Boolean,
        value: false
      },
      onlyOpenDown: {
        type: Boolean,
        value: false
      },
      showJobCreator: {
        type: Boolean,
        value: false
      },
      modelOptions: {
        type: Array
      },
      selectedCreator: {
        type: String
      },
      jobEditButton: {
        type: Boolean,
        value: false
      },
      noCreateNewJob: {
        type: Boolean,
        value: false
      },
      noShowArchived: {
        type: Boolean,
        value: false
      },
      offline: {
        type: Boolean,
        value: false
      },
      searchNotEmpty: {
        type: Boolean,
        value: false
      },
      searchBeginning: {
        type: Boolean,
        value: false
      },
      selected: {
        type: String,
        notify: true
      },
      selectOnFocus: {
        type: Boolean,
        value: false
      },
      showArchived: {
        type: Boolean,
        value: false
      },
      hideArchivedButton: {
        type: Boolean,
        value: false
      },
      userGroup: String,
      jobName: {
        type: String,
        notify: true,
        observer: 'jobNameLoaded'
      },
      loadJobData: {
        type: Boolean,
        value: false
      },
      defaultText: {
        type: String,
        value: 'Select a Job'
      },
      items: {
        type: Array,
        value: () => []
      },
      tooltipPosition: {
        type: String,
        value: 'bottom'
      },
      /* If given a value, it filters the list to only have jobs that include this string in the name.
      Also, the dialog for creating a new job will use this as the default name */
      filterByName: {
        type: String,
        value: ''
      },
      jobPermissionsDatabaseUrl: {
        type: String,
        value: () => {
          const onProduction = globalThis?.config?.appName == 'katapult-production';
          if (onProduction) {
            return getJobPermissionsDatabaseURL();
          }
        }
      }
    };
  }

  static get observers() {
    return [
      'updateItems(jobs.*, offline, userGroup)',
      'selectedJobRecordChanged(selectedJobRecord, selectedForListeners, jobName)',
      'filterJobs(jobs.*, userGroup, jobChooserOptions)',
      'controlShowArchived(noShowArchived, jobChooserOptions)',
      'updateLoading(loadingJobs, updatingItems, offline)',
      'updateLoadJobData(firebaseDisabled, opened, folderChooserOpened, forceLoadJobs)',
      'selectedChanged(selected, firebaseDisabled)'
    ];
  }

  static get importMeta() {
    return import.meta;
  }

  connectedCallback() {
    super.connectedCallback();

    this.katapultAuthInitializedListener = () => {
      katapultAuth.addEventListener('admin-changed', () => {
        this.controlShowArchived();
      });
      if (katapultAuth.admin != null) {
        this.controlShowArchived();
      }
    };

    /*global katapultAuth*/
    if (typeof katapultAuth != 'undefined') {
      this.controlShowArchived();
    } else {
      window.addEventListener('katapult-auth-initialized', this.katapultAuthInitializedListener, { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('katapult-auth-initialized', this.katapultAuthInitializedListener, { once: true });
  }

  constructor() {
    super();
    this.enabledFeatures = {};
  }

  ready() {
    super.ready();
  }

  async selectedChanged(selected, firebaseDisabled) {
    if (firebaseDisabled) return;
    // Check if the selected job is locked.
    if (selected) {
      const jobIsLocked = await isJobLocked(selected);
      if (jobIsLocked) {
        // If it is, show the locked job dialog.
        showLockedJobDialog(selected);
        this.selected = '';
        selected = '';
      }
    }

    // if we are in mobile, whenever the value for the selected job changes notify FirebaseWorker before setting the value that the listeners use
    if (self.isMobile && this.id == 'mobileJobChooser') await FirebaseWorker.setCurrentJobId(selected);
    this.selectedForListeners = selected;
  }

  sortJobs(a, b) {
    a = a.date_shared;
    b = b.date_shared;
    // prevent bad data from breaking the sort
    if (!isFinite(a) && !isFinite(b)) {
      return 0;
    }
    if (!isFinite(a)) {
      return 1;
    }
    if (!isFinite(b)) {
      return -1;
    }
    return b - a;
  }

  filterJobs() {
    const userHasAssignedToAllUsersAttribute = globalThis?.katapultAuth?.userRecord?.metaData?.assigned_to_all_users;
    if (userHasAssignedToAllUsersAttribute === true) {
      this.$.dropdown.filter = null;
      return;
    }
    if ((this.jobChooserOptions?.limit_job_chooser && globalThis?.katapultAuth?.admin != true) || this.filterByName) {
      if (this.jobChooserOptions?.limit_job_chooser?.type == 'user') {
        this.$.dropdown.filter = (x) => {
          if (x.metadata) {
            return x.metadata[this.jobChooserOptions.limit_job_chooser.property] == globalThis?.katapultAuth?.user?.uid;
          }
          return false;
        };
      } else if (this.filterByName) {
        this.$.dropdown.filter = (x) => {
          if (x.name) {
            return x.name.toLowerCase().includes(this.filterByName.toLowerCase());
          }
          return false;
        };
      }
    } else {
      this.$.dropdown.filter = null;
    }
  }

  toggleArchived() {
    this.showArchived = !this.showArchived;
    if (this.showArchived) {
      this.$.jobs.orderByChild = 'status';
      this.$.jobs.equalTo = null;
    } else {
      this.$.jobs.orderByChild = 'status';
      this.$.jobs.equalTo = 'active';
    }
    this.$.dropdown.temporarilyShowAllItems();
    this.$.dropdown.focus();
    // this.showAllItems = true;
  }

  updateLoading() {
    this.loading = (!this.offline && this.loadingJobs) || this.updatingItems;
  }

  updateLoadJobData() {
    this.loadJobData = (this.hasOpened || this.folderChooserOpened || this.forceLoadJobs) && !this.firebaseDisabled;
  }

  async createNewJob(e) {
    await import('./create-job-form.js');
    this.$.dropdown.close();
    this.$.createJobForm.open((success) => {
      this.selected = success.jobId;
    });
  }

  calcShowEditButton(show, id) {
    return show && id;
  }

  calcShowFolderChooser(selected, hideFolderChooser) {
    return (selected == '' || selected == null) && !hideFolderChooser;
  }

  isUnderlined(underline) {
    return underline ? 'underline' : '';
  }

  updateItems() {
    this.throttledUpdateItems =
      this.throttledUpdateItems ||
      Throttle(async () => {
        if (!this.opened && this.items.length) {
          this.needToUpdateJobs = true;
        } else {
          this.needToUpdateJobs = false;
          this.updatingItems = true;
          let jobs;
          if (this.offline && this.userGroup && this.syncing === false) {
            let offlineJobs = JSON.parse(localStorage.getItem('cachedJobs') || '[]');
            jobs = {};
            for (let i = 0; i < offlineJobs.length; i++) {
              let jobId = offlineJobs[i].jobId;
              let job = await FirebaseWorker.ref(`photoheight/job_permissions/${this.userGroup}/jobs/${jobId}`)
                .once('value')
                .then((s) => (s.exists() ? Object.assign(s.val(), { $key: jobId }) : null));
              if (job) jobs[jobId] = job;
            }
          } else {
            jobs = this.jobs;
          }
          let items = [];
          for (let jobId in jobs) {
            let job = jobs[jobId];
            if (job) {
              let item = { $key: jobId, name: job.name, icons: [], date_shared: job.date_shared, metadata: job.metadata };
              if (job.published) item.icons.push({ icon: 'social:public', size: 24 });
              if (job.status === 'archived') item.icons.push({ icon: 'archive', size: 24 });
              if (SquashNulls(this.jobChooserOptions, 'job_chooser_checkmark') && job.metadata) {
                if (
                  job.metadata[this.jobChooserOptions.job_chooser_checkmark.attribute] ===
                    this.jobChooserOptions.job_chooser_checkmark.value &&
                  this.jobChooserOptions.job_chooser_checkmark.attribute != null &&
                  this.jobChooserOptions.job_chooser_checkmark.value != null
                ) {
                  item.icons.push({ icon: 'check', size: 24, color: '#4caf50' });
                }
              }
              items.push(item);
            }
          }
          this.items = items;
          this.updatingItems = false;
          this.dispatchEvent(new CustomEvent('jobs-loaded'));
        }
      }, 1000);
    this.throttledUpdateItems.apply(arguments);
  }

  openedChanged() {
    if (this.opened) {
      this.hasOpened = true;
    }
    if (this.needToUpdateJobs) this.updateItems();
  }

  openJobEditor(e) {
    const mapPageElement = document.body.querySelector('katapult-maps-desktop') ?? document.body.querySelector('katapult-maps-mobile');
    mapPageElement.showJobEditor(e.currentTarget.jobId);
  }

  async openFolderChooser(e) {
    this.openingFolderChooser = true;
    await import('./project-folder-chooser.js');
    this.dispatchEvent(new CustomEvent('open-folder-chooser'));
  }

  async chooseNewJobLocation(e) {
    this.$.createJobForm.$.projectFolderChooser.chooseLocation(e.detail.newJobPath, this, (path) => {
      this.$.createJobForm.newJobPath = path;
    });
  }

  getProjectFolderName(projectFolder) {
    if (projectFolder == null || projectFolder == '') return 'Add to Project Folder';
    var pathSplit = projectFolder.replace(/(\/$|^\/)/g, '').split('/');
    pathSplit.unshift(SquashNulls(this.companyNames, pathSplit.shift()));
    return pathSplit.join('/');
  }

  displayToastMessage(e) {
    this.dispatchEvent(new CustomEvent('toast', { detail: e.detail }));
  }

  dropdownListChanged(e) {
    if (this.showAllItems) {
      this.showAllItems = false;
      e.currentTarget.temporarilyShowAllItems();
    }
  }

  selectedJobRecordChanged() {
    if (this.selectedJobRecord) {
      let copy = Path.copy(this.selectedJobRecord);
      copy.$key = this.selected;
      // Delay setting this so it doesn't get interfered with by the binding of this.selected to this.$.dropdown.value
      setTimeout(() => {
        this.$.dropdown.setSelectedItem(copy);
      });
    } else if (this.selectedForListeners && this.jobName) {
      let copy = { $key: this.selectedForListeners, name: this.jobName };
      // Delay setting this so it doesn't get interfered with by the binding of this.selected to this.$.dropdown.value
      setTimeout(() => {
        this.$.dropdown.setSelectedItem(copy);
      });
    }
  }

  // jobsChanged(e) {
  //   // Used to be called by update event on katapult firebase worker.
  //   // Since desktop uses regular firebase api, update event is never fired.
  //   let update = e.detail;
  //   if (Object.keys(update).some(path => Path.startsWith('/*/name', path, '/'))) {
  //     this.debounce('jobs-changed', function () {
  //       this.updateItems();
  //       this.fire('jobs-loaded');
  //     }, 100);
  //   }
  // }

  controlShowArchived() {
    if (typeof katapultAuth === 'undefined') return;
    let isAdmin = katapultAuth.admin;

    if (this.noShowArchived) {
      this.hideArchivedButton = true;
    } else if (this.jobChooserOptions?.show_archived_admin_only) {
      // Show the archived button if we are a company admin
      this.hideArchivedButton = !isAdmin;
    } else {
      this.hideArchivedButton = false;
    }
  }

  jobNameLoaded() {
    if (this.jobName != null) {
      this.$.dropdown.label = this.jobName;
    } else {
      this.$.dropdown.label = this.defaultText;
    }
    this.$.dropdown.updateSearchText();
  }

  updateSort() {
    this.$.dropdown.sort = this.alphabetical ? null : this.sortJobs;
  }

  userMayCreateJobs() {
    if (this.noCreateNewJob) return false;
    const limitJobCreationToServerOwner = this.limitJobCreationToServerOwner === true;
    if (!limitJobCreationToServerOwner) return true;
    const serverOwnerId = globalThis.config.firebaseData?.server_owner;
    const userIsServerOwner = serverOwnerId != null && this.userGroup == serverOwnerId;
    return userIsServerOwner === true;
  }

  trackSelectingJobs(e) {
    // Track selecting jobs from job chooser dropdown, only if enabled
    if (this.dataset.trackSelectingJobs === undefined) return;

    const jobId = e.detail.value;
    if (jobId) logger.info('Opened job from job list');
  }
}
customElements.define('job-chooser', JobChooser);
